import {
  BoundNumberField,
  column,
  Css,
  GridColumn,
  GridTable,
  Icon,
  IconKey,
  simpleHeader,
  SimpleHeaderAndData,
} from "@homebound/beam";
import { FieldState } from "@homebound/form-state";
import { camelCase } from "change-case";
import { ReactNode, useMemo } from "react";
import { Maybe } from "src/generated/graphqlTypes";

export type ProgramDataFeature = {
  label: string;
  base: FieldState<Maybe<number>>;
  min: Maybe<number>;
  max: Maybe<number>;
};

type ProgramDataCardProps = {
  title: string;
  icon: IconKey;
  /** Enums have no min/max, so they render above the table rather than as a feature row. */
  enumField?: ReactNode;
  features?: ProgramDataFeature[];
  disabled: ReactNode | undefined;
};

export function ProgramDataCard(props: ProgramDataCardProps) {
  const { title, icon, enumField, features, disabled } = props;
  const columns = useMemo(() => createColumns(disabled), [disabled]);
  const rows = useMemo(
    () => [simpleHeader, ...(features ?? []).map((f) => ({ id: f.label, kind: "data" as const, data: f }))],
    [features],
  );

  return (
    <div css={Css.df.fdc.gap1.$}>
      <div css={Css.df.aic.gap1.$}>
        <Icon icon={icon} />
        <h3 css={Css.mdSb.$}>{title}</h3>
      </div>
      {enumField && <div css={Css.py1.$}>{enumField}</div>}
      {features && (
        <GridTable
          id={`${camelCase(title)}Table`}
          columns={columns}
          rows={rows}
          style={{ bordered: true, allWhite: true, inlineEditing: true, rowHeight: "fixed" }}
        />
      )}
    </div>
  );
}

type Row = SimpleHeaderAndData<ProgramDataFeature>;

function createColumns(disabled: ReactNode | undefined): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Feature", data: ({ label }) => label }),
    column<Row>({
      header: "Base",
      data: ({ label, base }) => (
        <BoundNumberField label={label} labelStyle="hidden" field={base} disabled={disabled} />
      ),
      w: "100px",
    }),
    column<Row>({ header: "Min", data: ({ min }) => min ?? "—", w: "80px" }),
    column<Row>({ header: "Max", data: ({ max }) => max ?? "—", w: "80px" }),
  ];
}
